///////////////////////////////////////////////////////////////////////////////////
//
// Page-scroll lock guard.
//
// Bootstrap modals and SweetAlert stop the page from scrolling by writing to <body>:
// inline overflow / padding-right plus the marker classes (modal-open, swal2-shown).
// Both libraries only undo that from their own close callback, so anything that kills
// the overlay before the callback runs leaves the lock behind for the rest of the
// session. The common case is a URL change while a modal is open: the route component
// is destroyed, its modal element goes with it, and bootstrap's hide callback never
// fires. Nothing is left on screen to explain it either - index.html hides
// .modal-backdrop - the page just silently stops scrolling.
//
// Stacked modals corrupt it even when they do close normally: bootstrap saves whatever
// overflow <body> happens to have when a modal opens, so a second modal opening over a
// first one saves the first one's "hidden" as the value to restore. Once both are
// closed the app's own overflow:scroll (hardcoded on <body> in index.html) is gone.
// Customers2.fixHorizontalScroll() is an older local band-aid for that same corruption.
//
// releaseScrollLocks() puts <body> back to how index.html shipped it, but only when
// nothing on the page still wants the lock.
//
///////////////////////////////////////////////////////////////////////////////////


// index.html ships <body style="overflow:scroll"> so the scrollbar gutter is always
// there and the layout doesn't jump. Read it at import time, before any modal has had
// a chance to overwrite it.
const BASELINE_BODY_OVERFLOW = (document.body && document.body.style.overflow) || 'scroll';

// Passes after a URL change: right away (in case the old page was already torn down),
// then after bootstrap's 0.3s fade and once more well past it, since the overlay is
// usually still mid-hide - and still owns the lock - when the route switches.
const RELEASE_DELAYS = [0, 400, 1000];


// A swal that has started closing carries swal2-hide on its modal, and SweetAlert only
// drops its container (and the body classes) from the animationend handler - navigate
// during that animation and it never fires, so the container stays and body.swal2-shown,
// which is overflow-y:hidden, keeps the page locked with nothing visible on screen.
// A leftover like that is ours to clear; a swal that is genuinely up is not.
function swalIsLive(container) {
    const modal = container.querySelector('.swal2-modal');
    return !!modal && !modal.classList.contains('swal2-hide');
}


// An overlay that is open, or still opening, is entitled to keep the page locked.
function overlayIsOpen() {

    // a full-screen presentation the customer is looking at (מצב תצוגה ללקוח - CustomerPresentation,
    // InventoryPresentation) keeps the page under it still for as long as it is open, through its own URL changes
    // (the event switcher, the filters) too; it gives the page its scroll back when it goes
    if (document.querySelector('.customerPresentation, .inventoryPresentation'))
        return true;

    const swalContainers = document.querySelectorAll('.swal2-container');

    for (let i = 0; i < swalContainers.length; i++)
        if (swalIsLive(swalContainers[i]))
            return true;

    const modals = document.querySelectorAll('.modal');
    const hasBootstrap = typeof bootstrap != 'undefined' && bootstrap.Modal && bootstrap.Modal.getInstance;

    for (let i = 0; i < modals.length; i++) {

        if (modals[i].classList.contains('show'))
            return true;

        // bootstrap keeps the element displayed for the whole show/hide cycle and only adds
        // .show for the visible part of it. Computed, not inline, so a modal that something
        // displays from CSS counts as open too.
        if (getComputedStyle(modals[i]).display != 'none')
            return true;

        // show() locks the body first and only reveals the element once the backdrop has
        // faded in, so for ~150ms a modal that is opening looks closed by the two tests
        // above. Ask bootstrap itself. Only elements still in the DOM are looked at, so an
        // orphaned instance left behind by a destroyed component can't keep the lock alive.
        if (hasBootstrap) {
            const instance = bootstrap.Modal.getInstance(modals[i]);
            if (instance && instance._isShown)
                return true;
        }
    }

    return false;
}


// Returns true when it actually released something.
export function releaseScrollLocks() {

    const body = document.body;
    const html = document.documentElement;

    if (!body || overlayIsOpen())
        return false;

    // orphaned backdrops - invisible in this app, but they stack up one per leak
    const backdrops = document.querySelectorAll('.modal-backdrop');
    for (let i = 0; i < backdrops.length; i++)
        backdrops[i].parentNode.removeChild(backdrops[i]);

    // Any swal container still here is a leftover (a live one returned above). It has to
    // go, not just lose its classes: .swal2-container is a fixed, transparent, full-screen
    // z-index:1060 box, so it silently eats every click on the page. SweetAlert's own
    // init() drops a leftover container before opening the next alert, so this is safe.
    const swalContainers = document.querySelectorAll('.swal2-container');
    for (let i = 0; i < swalContainers.length; i++)
        swalContainers[i].parentNode.removeChild(swalContainers[i]);

    body.classList.remove('modal-open');
    body.classList.remove('swal2-shown');       // body.swal2-shown is overflow-y:hidden
    body.classList.remove('swal2-iosfix');      // and swal2-iosfix is position:fixed
    html.classList.remove('swal2-shown');

    // bootstrap's ScrollBarHelper bookkeeping. Left behind these make the NEXT modal
    // restore a stale value when it closes, so they go together with the lock itself.
    body.removeAttribute('data-bs-overflow');
    body.removeAttribute('data-bs-padding-right');

    body.style.removeProperty('padding-right');
    body.style.removeProperty('top');            // swal's iOS fix parks the scroll offset here
    body.style.overflow = BASELINE_BODY_OVERFLOW;

    if (html.style.overflow == 'hidden')
        html.style.removeProperty('overflow');

    return true;
}


export function installScrollLockGuard(router) {

    router.afterEach(() => {
        RELEASE_DELAYS.forEach(delay => setTimeout(releaseScrollLocks, delay));
    });

    // Heals the stacked-modal corruption without waiting for a URL change: when the last
    // modal of a stack closes, whatever the restore dance left on <body> is put back.
    document.addEventListener('hidden.bs.modal', () => {
        setTimeout(releaseScrollLocks, 0);
    });
}
